import type { JSX } from 'react';
import type { WebhookDeliveryResource } from '@majuscule/contracts';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import {
  Activity,
  Bell,
  Building2,
  CalendarDays,
  Columns3,
  ListTodo,
  LayoutDashboard,
  LogOut,
  Package,
  ReceiptEuro,
  Settings,
  ShieldCheck,
  SlidersVertical,
  Users,
  Webhook,
  type LucideIcon,
} from 'lucide-react';
import { signOut } from './auth';
import { type AppPath, type Crumb, Topbar, TopbarProvider } from './components/page-chrome';
import { useTodo } from './components/crm/shared';
import { internalFetch } from './lib/api';
import { type Permission, ROLE_LABEL, useMe } from './lib/me';
import { Avatar } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { TooltipProvider } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

interface NavEntry {
  to: AppPath;
  label: string;
  icon: LucideIcon;
  exact?: boolean;
  /** Compteur de ce qui demande une action (`nav-count`), si la donnée existe déjà. */
  count?: 'webhookFailures' | 'crmDue';
  /** Autres chemins qui activent l'entrée (fiche d'affaire sous Pipeline). */
  also?: readonly string[];
  /** Permission propre à l'entrée, en plus de celle du groupe (lot 20). */
  permission?: Permission;
}

interface NavGroup {
  title: string;
  /** Permission needed to see the group (lot 20): an employee only sees the CRM. */
  permission: Permission;
  entries: readonly NavEntry[];
}

/** Navigation de la console (NAVDEF de docs/design/screens/vx.js). */
const NAV: readonly NavGroup[] = [
  {
    title: 'Pilotage',
    permission: 'operations',
    entries: [
      { to: '/', label: "Vue d'ensemble", icon: LayoutDashboard, exact: true },
      { to: '/clients', label: 'Clients', icon: Users },
      { to: '/requests', label: 'Requêtes', icon: Activity },
    ],
  },
  {
    title: 'Commercial',
    permission: 'crm',
    entries: [
      { to: '/pipeline', label: 'Pipeline', icon: Columns3, also: ['/deals'] },
      { to: '/companies', label: 'Entreprises', icon: Building2 },
      { to: '/todo', label: 'À faire', icon: ListTodo, count: 'crmDue' },
    ],
  },
  {
    title: 'Configuration',
    permission: 'operations',
    entries: [
      { to: '/catalog', label: 'Catalogue', icon: Package },
      { to: '/use-cases', label: "Cas d'usage", icon: SlidersVertical },
      { to: '/appointment-types', label: 'Rendez-vous', icon: CalendarDays },
    ],
  },
  {
    title: 'Exploitation',
    permission: 'operations',
    entries: [
      { to: '/billing', label: 'Facturation', icon: ReceiptEuro },
      { to: '/webhooks', label: 'Webhooks', icon: Webhook, count: 'webhookFailures' },
      { to: '/alerts', label: 'Alertes', icon: Bell },
    ],
  },
  {
    title: 'Compte',
    permission: 'crm',
    entries: [
      { to: '/team', label: 'Équipe', icon: ShieldCheck, permission: 'team' },
      { to: '/settings', label: 'Paramètres', icon: Settings },
    ],
  },
];

function isActive(pathname: string, entry: NavEntry): boolean {
  if (entry.exact === true) {
    return pathname === entry.to;
  }
  return [entry.to, ...(entry.also ?? [])].some(
    (path) => pathname === path || pathname.startsWith(`${path}/`),
  );
}

function NavItem({
  entry,
  active,
  count,
}: {
  entry: NavEntry;
  active: boolean;
  count: number | null;
}): JSX.Element {
  const Icon = entry.icon;
  return (
    <Link
      to={entry.to}
      aria-current={active ? 'page' : undefined}
      className={cn(
        'flex h-8 items-center gap-2 rounded-md px-2 text-sm text-sidebar-foreground no-underline',
        'hover:bg-sidebar-accent hover:text-foreground',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-sidebar',
        active && 'bg-sidebar-accent font-medium text-foreground',
      )}
    >
      <Icon className="size-4 shrink-0" aria-hidden="true" />
      <span className="min-w-0 flex-1 truncate">{entry.label}</span>
      {count !== null && count > 0 ? (
        <span
          className="ml-auto rounded-full bg-muted px-1.5 font-mono text-xs font-medium leading-4 text-foreground"
          aria-label={`${count} à traiter`}
        >
          {count}
        </span>
      ) : null}
    </Link>
  );
}

function fallbackCrumbs(pathname: string): Crumb[] {
  for (const group of NAV) {
    for (const entry of group.entries) {
      if (isActive(pathname, entry)) {
        return [{ label: entry.label }];
      }
    }
  }
  return [];
}

/**
 * AppShell (components/AppShell, maquettes *.dc.html) : barre latérale de
 * 232px (marque, navigation groupée, opérateur), barre du haut (fil d'Ariane +
 * actions déclarées par la page via `<PageChrome>`), zone de travail
 * padding 24px, blocs espacés de 24px. Sous 760px, la barre latérale passe
 * au-dessus du contenu.
 */
export function AppLayout(): JSX.Element {
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const me = useMe();
  const permissions = me.data?.permissions ?? [];
  const isAdmin = permissions.includes('operations');
  // Même requête (et même cache) que la page Webhooks : livraisons en échec à rejouer.
  const deliveries = useQuery({
    queryKey: ['webhook-deliveries'],
    queryFn: () =>
      internalFetch<{ deliveries: WebhookDeliveryResource[] }>('/internal/webhooks/deliveries'),
    enabled: isAdmin,
  });

  // Même requête (et même cache) que la page À faire : rappels en retard et du jour.
  const todo = useTodo();
  const crmDue = todo.data?.dueCount ?? null;

  const queryClient = useQueryClient();
  const handleSignOut = async (): Promise<void> => {
    await signOut();
    // Another member may sign in next: drop the profile and data of this one.
    queryClient.clear();
    await navigate({ to: '/login' });
  };

  const email = me.data?.email ?? null;
  const name = me.data?.displayName ?? email;
  const avatar = name !== null ? initials(name) : '··';
  const webhookFailures =
    deliveries.data?.deliveries.filter((delivery) => delivery.status === 'failed').length ?? null;

  return (
    <TopbarProvider>
      <TooltipProvider delayDuration={300}>
        <div className="flex min-h-screen flex-wrap bg-background">
          <aside
            aria-label="Barre latérale"
            className={cn(
              'flex w-sidebar shrink-0 grow-0 basis-sidebar flex-col gap-4 border-r border-border bg-sidebar p-3 text-sidebar-foreground',
              'sticky top-0 h-screen overflow-y-auto',
              'max-[760px]:static max-[760px]:h-auto max-[760px]:w-auto max-[760px]:basis-full max-[760px]:border-b max-[760px]:border-r-0',
            )}
          >
            <div className="flex items-center px-2 py-1">
              <img src="/app_black.svg" alt="Majuscule" className="h-7 w-auto dark:hidden" />
              <img src="/app_white.svg" alt="Majuscule" className="hidden h-7 w-auto dark:block" />
            </div>

            <nav aria-label="Navigation principale" className="grid gap-0.5">
              {NAV.filter((group) => permissions.includes(group.permission)).map((group, index) => (
                <div key={group.title} className={cn('grid gap-0.5', index > 0 && 'mt-3')}>
                  <div className="mb-1 px-2 text-xs font-medium leading-4 text-sidebar-foreground opacity-75">
                    {group.title}
                  </div>
                  {group.entries
                    .filter((entry) => entry.permission === undefined || permissions.includes(entry.permission))
                    .map((entry) => (
                    <NavItem
                      key={entry.to}
                      entry={entry}
                      active={isActive(pathname, entry)}
                      count={
                        entry.count === 'webhookFailures'
                          ? webhookFailures
                          : entry.count === 'crmDue'
                            ? crmDue
                            : null
                      }
                    />
                  ))}
                </div>
              ))}
            </nav>

            <div className="mt-auto flex items-center gap-2 border-t border-border p-2">
              <Avatar>{avatar}</Avatar>
              <span className="min-w-0 flex-1">
                <b className="block truncate text-[13px] font-medium leading-[18px] text-foreground">
                  {name ?? 'Opérateur'}
                </b>
                <span className="block text-xs leading-4 text-muted-foreground">
                  {me.data === undefined ? 'Opérateur' : ROLE_LABEL[me.data.role]}
                </span>
              </span>
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label="Se déconnecter"
                title="Se déconnecter"
                onClick={() => void handleSignOut()}
              >
                <LogOut aria-hidden="true" />
              </Button>
            </div>
          </aside>

          <div className="flex min-w-0 shrink grow-[999] basis-[560px] flex-col">
            <Topbar fallbackCrumbs={fallbackCrumbs(pathname)} />
            <main className="grid min-w-0 content-start gap-6 p-6">
              <Outlet />
            </main>
          </div>
        </div>
      </TooltipProvider>
    </TopbarProvider>
  );
}

/** « Léo Munot » → « LM » ; un seul mot ou une adresse e-mail → ses deux premières lettres. */
function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter((word) => word !== '');
  const [first, second] = words;
  if (first !== undefined && second !== undefined && !first.includes('@')) {
    return `${first.charAt(0)}${second.charAt(0)}`.toUpperCase();
  }
  return name.trim().slice(0, 2).toUpperCase();
}
